import { createContext, useContext } from "react";

import type {
  AssetCommentThreadsBoolExp,
  AssetCommentThreadsQuery,
} from "src/graphql";

export type CanvasCommentThread =
  AssetCommentThreadsQuery["asset_comment_threads"][number];

/** Every comment on a canvas and its files in one predicate, in a fixed order so moving tiles doesn't change the query. */
export function canvasThreadsFilter(
  canvasFileId: string | undefined,
  fileIds: readonly string[],
): AssetCommentThreadsBoolExp {
  const ids = [...(canvasFileId ? [canvasFileId] : []), ...[...fileIds].sort()];
  return { amp_file_id: { _in: ids } };
}

/** The canvas's comments for its tiles: their threads from the canvas's one query, and which tile is open. */
export type CanvasComments = {
  /** The canvas's brand, which its files' review agents belong to. */
  brandProfileId: string | null;
  /** A file's threads, empty until the canvas's have loaded. */
  threadsFor: (fileId: string) => CanvasCommentThread[];
  /** The tile whose comments are open, on a thread or its list. A new object per pick. */
  openTile: { fileId: string; threadId: string | null } | null;
  setOpenTile: (
    openTile: { fileId: string; threadId: string | null } | null,
  ) => void;
};

const CanvasCommentsContext = createContext<CanvasComments | null>(null);

export const CanvasCommentsProvider = CanvasCommentsContext.Provider;

/** The canvas's comments, for a tile on it. */
export function useCanvasComments(): CanvasComments {
  const canvasComments = useContext(CanvasCommentsContext);
  if (!canvasComments) {
    throw new Error("useCanvasComments must be used inside a canvas");
  }
  return canvasComments;
}
